import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { applyTheme } from "@/lib/constants";
import { setTimeZone } from "@/lib/format";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [realUser, setUser] = useState(null);
  const [campaign, setCampaignState] = useState(() => sessionStorage.getItem("fcg-campaign"));
  const [viewAs, setViewAsState] = useState(() => sessionStorage.getItem("fcg-view-as") || null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onUnauthorised = () => setUser(false);
    window.addEventListener("fcg:unauthorised", onUnauthorised);
    return () => window.removeEventListener("fcg:unauthorised", onUnauthorised);
  }, [refresh]);

  const theme = realUser ? realUser.theme : localStorage.getItem("fcg-theme") || "system";
  useEffect(() => {
    applyTheme(theme);
    if (theme !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  useEffect(() => { if (realUser) setTimeZone(realUser.timezone); }, [realUser]);

  const setCampaign = useCallback((value) => {
    sessionStorage.setItem("fcg-campaign", value || "");
    setCampaignState(value || "");
  }, []);

  const setViewAs = useCallback((role) => {
    if (role) sessionStorage.setItem("fcg-view-as", role); else sessionStorage.removeItem("fcg-view-as");
    setViewAsState(role || null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* session already gone */
    }
    sessionStorage.removeItem("fcg-campaign");
    sessionStorage.removeItem("fcg-view-as");
    setViewAsState(null);
    setUser(false);
  }, []);

  const activeViewAs = realUser && realUser.role === "owner" ? viewAs : null;
  const user = useMemo(() => (realUser && activeViewAs ? { ...realUser, role: activeViewAs } : realUser), [realUser, activeViewAs]);
  const activeCampaign = campaign === null ? realUser?.current_campaign || "" : campaign;

  return (
    <AuthContext.Provider value={{ user, realUser, setUser, refresh, logout, campaign: activeCampaign, setCampaign, viewAs: activeViewAs, setViewAs }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
